import { CdkTrapFocus } from '@angular/cdk/a11y';
import { NgClass } from '@angular/common';
import {
    afterNextRender,
    AnimationCallbackEvent,
    AnimationFunction,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    Injector,
    input,
    signal,
    viewChildren,
} from '@angular/core';
import { GoogleAnalyticsService } from '@interacta-shared/data-access-common';
import { leaveWith } from '@interacta-shared/ui';
import { CarouselStep } from '@modules/carousel/models/carousel/carousel.model';
import { CarouselService } from '@modules/carousel/services/carousel.service';
import { TranslatePipe } from '@ngx-translate/core';
import { CarouselSlideComponent } from '../carousel-slide/carousel-slide.component';

@Component({
    selector: 'interacta-carousel-container',
    templateUrl: './carousel-container.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CarouselSlideComponent, CdkTrapFocus, NgClass, TranslatePipe],
})
export class CarouselContainerComponent {
    private carouselService = inject(CarouselService);
    private gaService = inject(GoogleAnalyticsService);
    private injector = inject(Injector);

    readonly canShowCarousel = input(false);

    readonly showCarousel = computed(() =>
        this.canShowCarousel() ? this.carouselService.getCarousel() : null,
    );

    readonly currentIndex = signal(0);

    private readonly slides = viewChildren(CarouselSlideComponent);

    // The dialog outlives the close by one animation: until it is inert,
    // ModalSurfaceService still reports it as the top modal and a toast
    // arriving meanwhile is announced inside it, then removed with it.
    readonly leave: AnimationFunction = (event: AnimationCallbackEvent) => {
        event.target.setAttribute('inert', '');
        leaveWith('animate-fade-out-slower')(event);
    };

    constructor() {
        effect(() => {
            const showCarousel = this.showCarousel();
            if (showCarousel) {
                this.gaService.sendPageView('Intro Carousel');
            }
        });
    }

    nextStep({ index, control }: CarouselStep): void {
        this.currentIndex.set(index);
        // The control that triggered the step sits on a slide that turns inert,
        // which drops focus to the body.
        afterNextRender(() => this.slides()[index]?.focusControl(control), {
            injector: this.injector,
        });
    }

    closeCarousel(): void {
        this.carouselService.markAsSeen();
        this.currentIndex.set(0);
    }
}
